import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from "@/firebase/config"; // Ensure recaptchaKey is exported
import { doc, getDoc } from "firebase/firestore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Clock, User, Hash, AlertTriangle, CheckCircle, ArrowRight, ShieldCheck, AlertCircle } from 'lucide-react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const StudentLanding = () => {
    const { issueId } = useParams();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [assessmentData, setAssessmentData] = useState<any>(null);
    const [studentName, setStudentName] = useState('');
    const [regNo, setRegNo] = useState('');

    useEffect(() => {
        const fetchAssessment = async () => {
            if (!issueId) return;
            
            // SIMULATION MODE
            if (issueId === 'simulation') {
                const simDataStr = sessionStorage.getItem('simulation_data');
                if (simDataStr) {
                    const simData = JSON.parse(simDataStr);
                    setAssessmentData({
                        ...simData,
                        active: true, // Force active
                        teacherName: simData.teacherName || "Simulated Teacher"
                    });
                } else {
                    setError("No simulation data found. Please return to dashboard and try again.");
                }
                setLoading(false);
                return;
            }

            try {
                const docRef = doc(db, 'issued_assessments', issueId);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    // Check Expiry
                    if (data.expiresAt && data.expiresAt.toMillis() < Date.now()) {
                        setError("This assessment link has expired.");
                        setLoading(false);
                        return;
                    }
                    if (!data.active) {
                        setError("This assessment is no longer active.");
                        setLoading(false);
                        return;
                    }
                    setAssessmentData(data);
                } else {
                    setError("Assessment not found. Please check the link.");
                }
            } catch (err) {
                console.error("Error fetching assessment:", err);
                setError("Failed to load assessment details.");
            } finally {
                setLoading(false);
            }
        };

        fetchAssessment();
    }, [issueId]);

    const { executeRecaptcha } = useGoogleReCaptcha();

    const handleStart = async () => {
        if (!studentName.trim() || !regNo.trim()) {
            setError("Please fill in all required fields.");
            return;
        }

        setLoading(true);

        try {
             // SIMULATION MODE CHECK
            if (issueId === 'simulation') {
                // Mock verification
            } else {
                if (!executeRecaptcha) {
                    setError("Security check not ready. Please reload.");
                    setLoading(false);
                    return;
                }
                const token = await executeRecaptcha('take_assessment');
                if (!token) {
                    setError("Security check failed. Please try again.");
                    setLoading(false);
                    return;
                }
                // (Optional) Verify token with backend here if we had a dedicated endpoint
                // For now, we trust the token generation implies basic humanity for this lightweight flow
            }

            // Save student details to sessionStorage
            sessionStorage.setItem(`session_${issueId}`, JSON.stringify({
                studentName,
                regNo,
                startTime: Date.now()
            }));

            navigate(`/assessment/run/${issueId}`);
        } catch (e) {
            console.error("Error starting assessment", e);
            setError("Failed to start. Please try again.");
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <p className="text-sm font-bold text-muted-foreground animate-pulse">Verifying Link...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 max-w-md mx-auto text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center text-red-600 mb-2">
                    <AlertCircle className="w-8 h-8" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Access Denied</h2>
                <p className="text-gray-600">{error}</p>
            </div>
        );
    }

    return (
        <Card className="shadow-lg border-t-4 border-t-blue-600 max-w-lg mx-auto animate-in fade-in zoom-in-95 duration-500">
            <CardHeader className="text-center pb-2">
                <div className="mx-auto bg-blue-50 text-blue-600 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                    <ShieldCheck className="w-6 h-6" />
                </div>
                <CardTitle className="text-2xl font-bold text-gray-900">{assessmentData?.subject}</CardTitle>
                <CardDescription className="font-medium text-gray-500">Issued by {assessmentData?.teacherName}</CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-6 pt-4">
                {/* Exam Metadata Piles */}
                <div className="grid grid-cols-2 gap-3 text-center">
                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                        <span className="block text-[10px] uppercase font-bold text-muted-foreground">Duration</span>
                        <span className="text-lg font-bold text-gray-900 flex items-center justify-center gap-1">
                            <Clock className="w-4 h-4 text-blue-500" />
                            {assessmentData?.config?.durationMinutes}m
                        </span>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                         <span className="block text-[10px] uppercase font-bold text-muted-foreground">Questions</span>
                         <span className="text-lg font-bold text-gray-900">
                            Check Inside
                         </span>
                    </div>
                </div>

                <div className="space-y-4 pt-2">
                    <div className="space-y-2">
                        <Label>Full Name <span className="text-red-500">*</span></Label>
                        <div className="relative">
                            <User className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                            <Input 
                                placeholder="Enter your full name" 
                                className="pl-9 h-11"
                                value={studentName}
                                onChange={(e) => setStudentName(e.target.value)}
                            />
                        </div>
                    </div>
                    
                    <div className="space-y-2">
                        <Label>Registration / Admission No. <span className="text-red-500">*</span></Label>
                        <Input 
                            placeholder="e.g. ADM/2023/001" 
                            className="h-11 font-mono uppercase"
                            value={regNo}
                            onChange={(e) => setRegNo(e.target.value)}
                        />
                    </div>
                    
                    {error && (
                        <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg flex items-center gap-2 animate-in slide-in-from-top-2">
                             <AlertTriangle className="w-4 h-4" /> {error}
                        </div>
                    )}
                </div>
            </CardContent>

            <CardFooter className="bg-gray-50/50 p-6 border-t border-gray-100">
                <Button 
                    size="lg" 
                    className="w-full h-12 text-md font-bold bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/20"
                    onClick={handleStart}
                    disabled={!studentName || !regNo}
                >
                    Start Assessment <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
            </CardFooter>
        </Card>
    );
};

export default StudentLanding;
